import { lazy, Suspense, type ReactNode } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { AppLayout } from '@/layouts/AppLayout'
import { RequireAnonymous, RequireAuth, RequirePermission } from './guards'
import { LoginPage } from '@/pages/login/LoginPage'
import { ChangePasswordPage } from '@/pages/login/ChangePasswordPage'
import { NotFoundPage } from '@/pages/errors/NotFoundPage'
import { NoScreensPage } from '@/pages/errors/NoScreensPage'
import { Spinner } from '@/components/ui/Spinner'

/**
 * Lazy-loads application routes to minimize entry chunk size.
 */
const OverviewPage = lazy(() =>
  import('@/pages/overview/OverviewPage').then((m) => ({ default: m.OverviewPage })),
)
const ChatbotPage = lazy(() =>
  import('@/pages/chatbot/ChatbotPage').then((m) => ({ default: m.ChatbotPage })),
)
const UsersPage = lazy(() =>
  import('@/pages/users/UsersPage').then((m) => ({ default: m.UsersPage })),
)
const SettingsPage = lazy(() =>
  import('@/pages/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })),
)
const AuditPage = lazy(() =>
  import('@/pages/audit/AuditPage').then((m) => ({ default: m.AuditPage })),
)
const VoiceAgentPage = lazy(() =>
  import('@/pages/voice/VoiceAgentPage').then((m) => ({ default: m.VoiceAgentPage })),
)

function Screen({ children }: { children: ReactNode }) {
  // A cached chunk resolves in the same tick, so this never paints on a warm
  // navigation — it is only for the cold first visit.
  return (
    <Suspense
      fallback={
        <div className="flex justify-center py-16">
          <Spinner size="lg" label="Loading" />
        </div>
      }
    >
      {children}
    </Suspense>
  )
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <RequireAnonymous>
        <LoginPage />
      </RequireAnonymous>
    ),
  },
  {
    // Outside AppLayout on purpose: while must_change_password is set there.
    path: '/change-password',
    element: (
      <RequireAuth>
        <ChangePasswordPage />
      </RequireAuth>
    ),
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      {
        index: true,
        element: (
          <RequirePermission anyOf={['overview.view']} what="the dashboard">
            <Screen><OverviewPage /></Screen>
          </RequirePermission>
        ),
      },
      {
        path: 'chatbot',
        element: (
          <RequirePermission anyOf={['leads.view', 'chats.view']} what="the chatbot">
            <Screen><ChatbotPage /></Screen>
          </RequirePermission>
        ),
      },
      {
        path: 'chatbot/leads/:id',
        element: (
          <RequirePermission anyOf={['leads.view']} what="leads">
            <Screen><ChatbotPage /></Screen>
          </RequirePermission>
        ),
      },
      {
        path: 'chatbot/chats/:sessionId',
        element: (
          <RequirePermission anyOf={['chats.view']} what="chats">
            <Screen><ChatbotPage /></Screen>
          </RequirePermission>
        ),
      },
      {
        path: 'voice',
        element: (
          <RequirePermission anyOf={['voice.view']} what="the voice agent">
            <Screen><VoiceAgentPage /></Screen>
          </RequirePermission>
        ),
      },
      {
        path: 'users',
        element: (
          <RequirePermission anyOf={['users.view']} what="user management">
            <Screen><UsersPage /></Screen>
          </RequirePermission>
        ),
      },
      {
        path: 'settings',
        element: (
          <RequirePermission anyOf={['settings.view']} what="settings">
            <Screen><SettingsPage /></Screen>
          </RequirePermission>
        ),
      },
      {
        path: 'audit',
        element: (
          <RequirePermission anyOf={['audit.view']} what="the audit log">
            <Screen><AuditPage /></Screen>
          </RequirePermission>
        ),
      },
      { path: 'no-access', element: <NoScreensPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
])
